"use client"

import React, { useActionState, useState } from "react"
import Form from "next/form"
import Link from "next/link"
import { redirect } from "next/navigation"
import { addBasketItemsToWishlistAction, createWishlistAction } from "@/actions/wishlistActions"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { zodResolver } from "@hookform/resolvers/zod"
import { ClipboardList, LoaderCircle } from "lucide-react"
import { useForm } from "react-hook-form"

import { hasEntries } from "@/lib/helper"
import { getLinkWithPrefixFromURLAndLocale } from "@/lib/link-utils/link-utils"
import {
  Button,
  buttonVariants,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
} from "@/components/ui/components"
import { cn } from "@/components/ui/helpers/utils"
import { UbBasketCreateWishlistActionStoryblok } from "@/components/storyblok/component-types"
import { CreateWishlistSchema, safeParse } from "@/components/wishlist/CreateWishlist"

interface BasketCreateWishlistClientProps {
  blok: UbBasketCreateWishlistActionStoryblok
  isAnonymous: boolean
  sbLocale: string
}

export default function BasketCreateWishlistActionClient(props: BasketCreateWishlistClientProps) {
  const { basketLineItems } = useUbStorefrontState()

  const [createDialogIsOpen, setCreateDialogIsOpen] = useState(false)
  const [confirmDialogIsOpen, setConfirmDialogIsOpen] = useState(false)
  const [confirmDialogMessage, setConfirmDialogMessage] = useState(props.blok.confirm_dialog_message)

  const [wishlistName, setWishlistName] = useState("")
  const [wishlistId, setWishlistId] = useState("")

  const resolver = zodResolver(CreateWishlistSchema)

  async function formAction(initialState: any, formData: FormData) {
    const values = {
      name: formData.get("name") as string,
    }
    const errors = safeParse(values)
    if (!hasEntries(errors)) {
      const wishlistName = values.name
      createWishlistAction(wishlistName)
        .then((wishlistId) => {
          addBasketItemsToWishlistAction(wishlistId, basketLineItems)
          return wishlistId
        })
        .then((wishlistId) => {
          setConfirmDialogMessage(
            confirmDialogMessage
              ? JSON.parse(JSON.stringify(confirmDialogMessage).replace("{0}", wishlistName))
              : confirmDialogMessage
          )
          setWishlistName(wishlistName)
          setWishlistId(wishlistId)
          setCreateDialogIsOpen(false)
          setConfirmDialogIsOpen(true)
        })
        .catch((err) => {
          console.error(err, wishlistName)
          errors["name"] = {}
        })
    }
    return {
      values,
      errors,
    }
  }

  const [state, action, isPending] = useActionState(formAction, {})

  const { formState, register } = useForm({
    errors: state.errors,
    mode: "onChange",
    resolver,
  })

  function onCreateDialogOpenChange(open: boolean) {
    if (open && props.isAnonymous) {
      const wishlistRedirectUrl = getLinkWithPrefixFromURLAndLocale("/auth/signin", props.sbLocale)
      redirect(`${wishlistRedirectUrl}?loginRedirectUrl=${encodeURIComponent(window.location.href)}`)
    }
    setCreateDialogIsOpen(open)
  }

  return (
    props.blok.enabled && (
      <>
        <Dialog open={createDialogIsOpen} onOpenChange={onCreateDialogOpenChange}>
          <DialogTrigger className={cn(buttonVariants({ variant: "outline_secondary", size: "defaultNoGrow" }))}>
            <div className={"flex w-full flex-row items-center justify-between gap-1"}>
              <p>{props.blok.label}</p>
              <ClipboardList />
            </div>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{props.blok.create_dialog_title}</DialogTitle>
            </DialogHeader>
            <Form className={"flex flex-col gap-4"} action={action}>
              <Input type="text" placeholder={props.blok.create_dialog_name_placeholder} {...register("name")} />
              {formState.errors.name ? (
                <p className={"text-sm text-primary"} role="alert">
                  {formState.errors.name.type === "too_big"
                    ? props.blok.create_dialog_name_too_big
                    : props.blok.create_dialog_name_too_small}
                </p>
              ) : null}
              <Button className={cn(buttonVariants({ size: "default" }))} disabled={isPending}>
                {!isPending ? (
                  <span>{props.blok.create_dialog_submit}</span>
                ) : (
                  <LoaderCircle className={"animate-spin"} />
                )}
              </Button>
            </Form>
          </DialogContent>
        </Dialog>
        <Dialog open={confirmDialogIsOpen} onOpenChange={setConfirmDialogIsOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{props.blok.confirm_dialog_title}</DialogTitle>
              <DialogDescription>
                {confirmDialogMessage}
                <br />
                {props.blok.confirm_dialog_link_label && wishlistName && wishlistId && (
                  <>
                    {props.blok.confirm_dialog_link_label}{" "}
                    <Link href={`/account/wishlist/${wishlistId}`} prefetch={false} className={"underline"}>
                      {wishlistName}
                    </Link>
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogClose className={cn(buttonVariants({ size: "default" }))}>
              {props.blok.confirm_dialog_submit}
            </DialogClose>
          </DialogContent>
        </Dialog>
      </>
    )
  )
}
